import { Order } from "@/api/orders.api";
import { Card } from "@/components/ui/card";
import { formatOrderDateTime } from "@/utils/date.utils";
import { formatMonetaryAmount } from "@/utils/numbers.utils";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { UserAvatar } from "@/components/core/UserAvatar";
import { ExtendedListChildComponentProps } from "@/components/core/InfiniteScrollList/VirtualizedList";
import { GroupPosition, resolveGroupPosition } from "@/components/core/InfiniteScrollList/groupPosition";
import { GroupConnector } from "@/components/core/InfiniteScrollList/GroupConnector";
import { CurrencyAmount } from "@/components/ui/CurrencyAmount";
import { useIsPermitted } from "@/hooks/useIsPermitted";
import { ROLES } from "@/common/permissions";
import { TestBanner } from "@/components/core/TestBanner";
import { CheckCircle2 } from "lucide-react";
import { OrderPosDetails } from "@/components/list-cards/orders/OrderPosDetails";

interface OrderListCardProps extends ExtendedListChildComponentProps {
  isSelectable?: boolean;
  isSelected?: boolean;
  onSelect?: (order: Order) => void;
}

export function OrderListCard({ 
  style, 
  data, 
  ref,
  isSelectable = false,
  isSelected = false,
  onSelect,
  prevItem,
  nextItem,
}: OrderListCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isPermitted = useIsPermitted(ROLES.MANAGER);

  if (!data) {
    return <div style={style} className="px-4" ref={ref} />;
  }

  const order = data as Order;
  const groupPosition = resolveGroupPosition(
    order.posGroupId,
    (prevItem as Order | undefined)?.posGroupId,
    (nextItem as Order | undefined)?.posGroupId,
  );

  const handleClick = () => {
    if (isSelectable && onSelect) {
      onSelect(order);
    } else if (isPermitted) {
      navigate(`/members/${order.mezzaMemberId}`);
    }
  };

  return (
    <div style={style}>
      <Card
        className={cn(
          "transition-all duration-150 p-0 pb-4 relative",
          "rounded-none border-0 border-b shadow-none",
          isSelectable && "cursor-pointer active:bg-muted/20",
          isSelected && "bg-primary/5"
        )}
        onClick={handleClick}
        ref={ref}
      >
        {isSelected && groupPosition === GroupPosition.None && (
          <div className="absolute left-0 top-0 h-full w-1 bg-primary rounded-r-sm" />
        )}

        <GroupConnector position={groupPosition} />

        <div className="flex items-start justify-between gap-3 pl-4 pr-4">
          <div className="flex items-center gap-3 overflow-hidden">
            {isSelectable && (
              <div className="flex-shrink-0">
                <div className={cn(
                  "h-5 w-5 border-2 rounded-sm transition-all duration-150 flex items-center justify-center",
                  isSelected 
                    ? "bg-primary border-primary" 
                    : "border-muted-foreground/30 bg-background"
                )}>
                  {isSelected && (
                    <CheckCircle2 className="h-3.5 w-3.5 text-primary-foreground" strokeWidth={3} />
                  )}
                </div>
              </div>
            )}
            
            <UserAvatar
              imageUrl={order.customerImageUrl}
              name={order.customerName}
            />

            <div className="flex flex-col min-w-0">
              <div className="flex items-center gap-2">
                <span
                  className={cn(
                    "font-medium text-base truncate",
                    !isPermitted && "cursor-default"
                  )}
                >
                  {order.customerName}
                </span>
                
                {order.isTestOrder && <TestBanner />}
              </div>

              <span className="text-xs text-muted-foreground">
                {formatOrderDateTime(order.paidDate)}
              </span>

              <span className="text-xs text-muted-foreground truncate">
                {order.locationName}
              </span>
            </div>
          </div>

          <div className="text-right flex flex-col flex-shrink-0">
            <div className="font-bold text-base whitespace-nowrap tabular-nums">
              <CurrencyAmount
                amount={formatMonetaryAmount(order.amount + order.tipAmount)}
                currency={order.currency}
              />
            </div>

            <div className="text-xs text-muted-foreground tabular-nums whitespace-nowrap">
              <CurrencyAmount
                amount={formatMonetaryAmount(order.amount)}
                currency={order.currency}
              />{" "}
              {t("orders.bill")}
            </div>

            <div className="text-xs text-muted-foreground tabular-nums whitespace-nowrap">
              <CurrencyAmount
                amount={formatMonetaryAmount(order.tipAmount)}
                currency={order.currency}
              />{" "}
              {t("orders.tip")}
            </div>

            <div className="text-xs text-muted-foreground font-bold tabular-nums">
              #{order.simpleIdentifier}
            </div>

            <OrderPosDetails order={order} />
          </div>
        </div>
      </Card>
    </div>
  );
}
